<template>
  <div class="document-container" style="padding-top: 0">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Election Submission Results (Step 3 for Elections)</div>
          </template>
          <template #actions>
            <ul v-if="schoolYear === currentSchoolYear">
              <li class="p-narrow no-border">
                <div class="list-content">
                  <a
                    @click="reloadElectionData"
                    :class="{ 'disabled':
                        loading
                        || loadingSummary
                        || loadingSummaryError
                        || !electionsPermissions.canAccess
                        || cepSummary.electionStatus === 'A' }"
                    title="Click to refresh data.">
                    <font-awesome-icon :icon="['fas', 'sync-alt']" class="lg"/>
                  </a>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <es-loader v-if="loading || loadingSummary"/>
      <es-exception
        v-else-if="schoolYear === currentSchoolYear && !electionsPermissions.canAccess"
        exceptionMessage="Current year Elections: Submission data cannot be
          viewed or updated until the State accepts the current year Population Data."
        margin="0rem"
        style="padding: 6.5rem"/>
      <es-exception
        v-else-if="schoolYear === currentSchoolYear && !electionsPermissions.submissionReady"
        exceptionMessage="The Elections – Recalculate form must be completed and submitted to
          view Election: Submission data. Please access the Elections – Recalculate tab and
          save the appropriate options for each site."
        margin="0rem"
        style="padding: 6.5rem"/>
      <div v-else class="cep-submission-summary-content">
        <div class="has-text-left banner">
          The Elections data below is based upon the selections in the
          Elections – Recalculate worksheet. These numbers should be reviewed
          and verified before submitting the data.
        </div>
        <div class="table-container">
          <table class="table is-hoverable is-fullwidth">
            <colgroup>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col/>
              <col width="1%"/>
            </colgroup>
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"
            />
            <tbody v-if="!locations.length">
              <tr>
                <td colspan="100%" style="text-align: center">
                  <es-exception exceptionMessage="No results found."/>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="loadingError || loadingSummaryError">
              <tr>
                <td colspan="100%" style="text-align: center">
                  <es-exception
                    exceptionMessage="There was an issue.
                      Please try again or contact your administrator."/>
                </td>
              </tr>
            </tbody>
            <template v-else>
              <cep-manager-elections-summary-location-row
                v-for="(location, index) in locations"
                :key="index"
                :cepSummary="cepSummary"
                :electionDate="isElectionDateInvalid"
                :location="location"
                :locationGroupId="locationGroupId"
                :schoolYear="schoolYear"
                @reload="onAction"/>
              <tfoot>
                <tr>
                  <td colspan="100%">
                    <es-pager :totalCount="totalCount"/>
                  </td>
                </tr>
              </tfoot>
            </template>
          </table>
        </div>
        <div class="cep-submission-summary-statistics">
          <es-loader v-if="loadingSubmissionSummary"/>
          <es-exception
            v-else-if="loadingSubmissionSummaryError"
            exceptionMessage="There was an issue loading the summary.
              Please try again or contact your administrator."/>
          <es-profile-header v-else class="is-inline">
            <template #profile-overview>
              <div class="columns">
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.continueBaseYearCount === 'number' ?
                        summary.continueBaseYearCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Continue Base Year <br /> Count
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.createNewBaseYearCount === 'number' ?
                        summary.createNewBaseYearCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Create New Base Year <br /> Count
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.graceYearCount === 'number' ?
                        summary.graceYearCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Grace Year <br /> Count
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.notParticipatingCount === 'number' ?
                        summary.notParticipatingCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Not Participating <br /> Count
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.individualParticipatingCount === 'number' ?
                        summary.individualParticipatingCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Individual Participation <br /> Count
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ typeof summary.clusterCount === 'number' ?
                        summary.clusterCount : 'N/A' }}
                    </div>
                    <div class="column-subtitle">
                      Cluster <br /> Count
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </es-profile-header>
        </div>
        <div
          v-if="
            !loading
            && !loadingSummary
            && !loadingError
            && !loadingSummaryError
            && schoolYear === currentSchoolYear
            && !isStateUser
            && locations.length
            && hasModule('DRMCH:CEP:ELECTIONS:SUBMIT')"
          class="certify-row is-flex"
        >
          <div class="certify-description is-pulled-right is-flex flex-direction--column">
            <label class="checkbox has-text-left">
              <input
                :disabled="checkSubmissionStatus"
                type="checkbox"
                style="margin-right:0.5rem"
                v-model="certified"
                @click="resetCertify">
              I certify the information entered for all schools in this application is correct and,
              if participating, will be used for CEP election data if applicable.
            </label>
            <div
              v-if="certifyError"
              class="certify-text help is-danger has-text-left">
              Please check the checkbox to proceed to submit and certify election data.
            </div>
          </div>
          <button
            class="button is-primary"
            @click="submitElections"
            :disabled="checkSubmissionStatus">
            Submit &amp; Certify Elections
          </button>
        </div>
        <div
          v-if="
            !loading
            && !loadingError
            && !loadingSummaryError
            && schoolYear === currentSchoolYear
            && isStateUser
            && locations.length
            && hasModule('DRMCH:CEP:ELECTIONS:APPROVE')"
          class="certify-row is-flex">
          <button
            class="button is-primary"
            @click="reopenSubmission"
            :disabled="checkReopenStatus">
            Reopen Submission
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import {
  faCheckCircle,
  faCalculator,
  faDotCircle,
  faExclamationCircle,
  faSyncAlt,
  faInfoSquare,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCheck,
  faBan,
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';

import CepManagerElectionsSummaryLocationRow from '@/components/CepManagerElectionsSummaryLocationRow.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import EsProfileHeader from '@/components/EsProfileHeader.vue';

library.add(
  faAngleDoubleRight,
  faBan,
  faCalculator,
  faCheck,
  faCheckCircle,
  faDotCircle,
  faInfoSquare,
  faExclamationCircle,
  faExclamationTriangle,
  faSyncAlt,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    CepManagerElectionsSummaryLocationRow,
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
    EsProfileHeader,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    page: {
      type: Number,
      default: 1,
    },
    schoolYear: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'locationName',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    electionsPermissions: {
      type: Object,
    },
  },
  data() {
    return {
      certified: null,
      certifyError: null,
      loading: true,
      loadingError: null,
      loadingSubmissionSummary: false,
      loadingSubmissionSummaryError: false,
      locations: [],
      totalCount: null,
      apiSorter: {},
      columnHeaders: [],
      summary: null,
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.getData();

    this.columnHeaders = [
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
        field: 'localCode',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Location Name',
        field: 'locationName',
        order: 'ASC',
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Base Year',
        field: 'baseYear',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Total Sites',
        field: 'siteCount',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'Total Identified',
        field: 'baseYearTotalIdentified',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Total Enrolled',
        field: 'baseYearTotalEnrolled',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'ISP %',
        field: 'totalISP',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Calculated ISP %',
        field: 'calculatedISP',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Participation Previous | New',
        field: 'participationPrevNew',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
        },
      },
      {
        header: 'Election Status',
        field: 'electionStatus',
        order: null,
        sortable: true,
        style: {
          padding: '.35rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'Decision',
        style: {
          padding: '.35rem 0.75rem',
          borderRight: '1px solid #dee2e6',
        },
      },
      {
        header: 'Actions',
        style: {
          padding: '.35rem 0.75rem',
        },
      },
    ];
  },
  watch: {
    $route() {
      this.getData();
      if (this.certified) {
        this.certified = false;
      }
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    ...mapGetters({
      isStateUser: 'CEPElectionApprovalUser',
      properties: 'propertiesModule/properties',
    }),
    checkSubmissionStatus() {
      const { electionStatus } = this.cepSummary;

      return (
        this.isElectionDateInvalid
        || (this.isStateUser && electionStatus !== 'S')
        || (!this.isStateUser
          && (
            electionStatus === 'S'
            || electionStatus === 'A'
            || electionStatus === 'R'
          ))
        || this.cepSummary.recalculateStatus === 'I');
    },
    checkReopenStatus() {
      const { electionStatus } = this.cepSummary;
      return (
        this.isElectionDateInvalid
        || electionStatus !== 'S'
        || this.cepSummary.recalculateStatus !== 'C'
      );
    },
    currentSchoolYear() {
      return Number(this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR']);
    },
    electionStatus() {
      return this.cepSummary.electionStatus;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    recalculateStatus() {
      return this.cepSummary.recalculateStatus;
    },
    isElectionDateInvalid() {
      const today = new Date().getTime();
      return moment(today).isBefore(this.properties['CEP-POPULATION_SUBMISSION_DUE_DATE'])
        || moment(today).isAfter(this.properties['CEP-ELECTION_SUBMISSION_DUE_DATE'], 'day');
    },
    searchParams() {
      const params = {};

      if (this.locationGroupId) {
        params.locationGroupId = this.locationGroupId;
      }
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};
      params.pageNum = this.page ? this.page : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async approveSchool(schoolId) {
      try {
        await axios.put(`cep/${this.locationGroupId}/population/approve?schoolId=${schoolId}`);
        this.showNotification('success', ['Population data is accepted.']);
        this.getData();
      } catch (error) {
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was a problem acccepting population data. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        }
      }
    },
    async getLocations() {
      // Prevent API call if required params are missing
      if (!this.locationGroupId || !this.schoolYear) {
        this.locations = [];
        return;
      }
      try {
        this.loading = true;
        this.loadingError = false;

        const response = await axios
          .get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/submission-results`,
            {
              params: this.urlParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });
        this.locations = response.data.data;
        this.totalCount = response.data.totalCount;
        [this.apiSorter] = response.data.sorters;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async getSummary() {
      // Prevent API call if required params are missing
      if (!this.locationGroupId || !this.schoolYear) {
        this.summary = null;
        return;
      }
      try {
        this.loadingSubmissionSummary = true;
        this.loadingSubmissionSummaryError = false;

        const response = await axios.get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/election-summary`);
        this.summary = response.data;
      } catch (e) {
        this.loadingSubmissionSummaryError = true;
      } finally {
        this.loadingSubmissionSummary = false;
      }
    },
    getData() {
      this.getLocations();
      this.getSummary();
    },
    reloadElectionData() {
      this.refreshSummarySection();
      this.getData();
    },
    async rejectSchool(schoolId) {
      try {
        await axios.put(`cep/${this.locationGroupId}/population/reject?schoolId=${schoolId}`);
        this.showNotification('success', ['Population data is rejected.']);
        this.getData();
      } catch (error) {
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was a problem rejecting population data. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        }
      }
    },
    async reopenSubmission() {
      try {
        await axios.put(`cep/${this.locationGroupId}/elections/reopen-submission`);
        this.showNotification('success', ['Elections submission is now permitted for LEAs.']);
        this.refreshSummarySection();
        this.getData();
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 403) {
          apiMsg = 'Sorry, you are not authorized to access this content.';
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      }
    },
    onAction() {
      this.getData();
      this.refreshSummarySection();
    },
    refreshSummarySection() {
      this.$emit('change');
    },
    resetCertify() {
      this.hideNotification();
      this.certifyError = false;
    },
    async submitElections() {
      if (this.certified) {
        try {
          await axios.put(`cep/${this.locationGroupId}/elections/certify`);
          this.showNotification('success', ['Election data has been submitted successfully.']);
          this.refreshSummarySection();
          this.getData();
        } catch (error) {
          const { status } = error.response;
          if (status === 400) {
            this.showNotification('danger', [`There was a problem submitting election data. ${error.response.data.message}`]);
          } else {
            this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
          }
        }
      } else {
        this.showNotification('danger', ['Please check the checkbox to proceed to submit and certify election data.']);
        this.certifyError = true;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
  },
};
</script>

<style lang="sass" scoped>
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .certify-row
    justify-content: flex-end
    align-items: center
  .certify-description
    margin-right: 1rem
  .border-right
    border-right: 1px solid #dee2e6
  /deep/ .column.is-three-fifths.header-left
    width: auto
    flex-grow: 1
  .cep-submission-summary-statistics
    margin-bottom: 1rem
  .banner
    font-size: .85rem
    padding-left: 1.5rem
    padding-right: 0.5rem
    margin-bottom: 1rem
    color: #212529
  .cep-submission-summary-content
    padding: 1rem
  .table-container
    overflow-x: auto
    /deep/ .es-sorter a
      display: flex
      align-items: center
      svg
        margin-left: 5px
</style>
